import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  MessageSquare,
  Hash,
  Users,
  Pin,
  Bell,
  Search,
  Plus,
  Send,
  Smile,
  Paperclip,
  AtSign,
  CheckCheck,
  Circle,
} from "lucide-react";

const channels = [
  { name: "general", icon: Hash, unread: 0, active: true },
  { name: "front-desk", icon: Hash, unread: 3 },
  { name: "hygiene", icon: Hash, unread: 0 },
  { name: "billing", icon: Hash, unread: 1 },
  { name: "dr-martinez", icon: Circle, unread: 0, isDM: true },
  { name: "dr-patel", icon: Circle, unread: 2, isDM: true },
];

const messages = [
  {
    id: 1,
    sender: "Jessica",
    role: "Front Desk",
    avatar: "JR",
    color: "bg-blue-500",
    time: "9:02 AM",
    content:
      "Heads up — Mrs. Chen is running 15 min late for her 9:30. Should I push her back or keep as-is?",
  },
  {
    id: 2,
    sender: "Dr. Martinez",
    role: "Dentist",
    avatar: "DM",
    color: "bg-indigo-500",
    time: "9:03 AM",
    content:
      "Keep it. I can start with the X-rays for my 9:45 patient while we wait. @Sarah can you pull up her pano?",
  },
  {
    id: 3,
    sender: "Sarah",
    role: "Hygienist",
    avatar: "SK",
    color: "bg-emerald-500",
    time: "9:04 AM",
    content: "Already on it! Pano is loaded in Op 3 🙌",
  },
  {
    id: 4,
    sender: "Amanda (AI)",
    role: "Insurance Verification",
    avatar: "AI",
    color: "bg-violet-500",
    time: "9:05 AM",
    content:
      "FYI — Verified insurance for the 10:00 AM patient (James Davis). Delta Dental PPO, $1,200 remaining annual max. Breakdown attached to his chart.",
    isAI: true,
  },
  {
    id: 5,
    sender: "Jessica",
    role: "Front Desk",
    avatar: "JR",
    color: "bg-blue-500",
    time: "9:06 AM",
    content: "Perfect timing, thanks Amanda! Dr. P — Op 2 is prepped for your 9:15.",
  },
];

const TeamChatSlide = () => {
  const [visibleMessages, setVisibleMessages] = useState([]);

  useEffect(() => {
    setVisibleMessages([messages[0], messages[1]]);
    const remaining = messages.slice(2);
    const timeouts = remaining.map((msg, i) =>
      setTimeout(() => {
        setVisibleMessages((prev) => [...prev, msg]);
      }, 1200 + i * 1800)
    );
    return () => timeouts.forEach(clearTimeout);
  }, []);

  return (
    <div className="h-full w-full bg-slate-900 flex items-center justify-center p-12 relative overflow-hidden text-white">
      {/* Background Elements */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-violet-600/20 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-blue-600/20 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/2" />

      <div className="flex flex-row h-full w-full max-w-[1500px] gap-12 relative z-10">
        {/* Left Side: Context */}
        <div className="w-2/5 flex flex-col justify-center space-y-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-violet-500/20 text-violet-300 border border-violet-500/30 text-sm font-semibold w-fit">
            <MessageSquare className="w-4 h-4" />
            <span>Team Chat</span>
          </div>

          <h2 className="text-4xl lg:text-5xl font-bold leading-tight">
            Your Practice's{" "}
            <span className="text-violet-400">Internal Hub.</span>
          </h2>

          <p className="text-lg text-slate-300 leading-relaxed">
            Secure, real-time messaging built for dental teams. Channels for
            each department, direct messages, and AI agent updates — all in one
            place. No more shouting across the office or sticky notes.
          </p>

          <div className="space-y-4 pt-4">
            <div className="flex items-start gap-4">
              <div className="p-2 bg-violet-500/20 rounded-lg text-violet-400 mt-1">
                <Hash className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">
                  Organized Channels
                </h3>
                <p className="text-slate-400 text-sm">
                  #front-desk, #hygiene, #billing — keep conversations organized
                  by team or topic.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="p-2 bg-blue-500/20 rounded-lg text-blue-400 mt-1">
                <AtSign className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">
                  @Mentions & Notifications
                </h3>
                <p className="text-slate-400 text-sm">
                  Tag specific team members or the whole channel. Desktop &
                  mobile push notifications.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="p-2 bg-emerald-500/20 rounded-lg text-emerald-400 mt-1">
                <Users className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">
                  AI Agents Post Updates
                </h3>
                <p className="text-slate-400 text-sm">
                  Insurance verifications, call summaries, and schedule changes
                  surface automatically in your team chat.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side: Chat Interface */}
        <div className="w-3/5 h-full flex items-center">
          <motion.div
            initial={{ x: 40, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="w-full h-[680px] bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden flex flex-row text-slate-900"
          >
            {/* Sidebar */}
            <div className="w-56 bg-slate-800 text-white flex flex-col flex-shrink-0 border-r border-slate-700">
              {/* Workspace Header */}
              <div className="p-4 border-b border-slate-700">
                <h3 className="font-bold text-base truncate">
                  Asha Dental
                </h3>
                <p className="text-xs text-slate-400 mt-0.5">
                  12 members online
                </p>
              </div>

              {/* Channels */}
              <div className="flex-1 overflow-y-auto p-3 space-y-1">
                <div className="flex items-center justify-between px-2 mb-2">
                  <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                    Channels
                  </span>
                  <Plus className="w-3 h-3 text-slate-500" />
                </div>
                {channels
                  .filter((c) => !c.isDM)
                  .map((ch, i) => (
                    <button
                      key={i}
                      className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm transition-colors ${
                        ch.active
                          ? "bg-blue-600/30 text-white"
                          : "text-slate-400 hover:bg-slate-700/50 hover:text-slate-200"
                      }`}
                    >
                      <Hash className="w-3.5 h-3.5 flex-shrink-0 opacity-60" />
                      <span className="truncate font-medium">{ch.name}</span>
                      {ch.unread > 0 && (
                        <span className="ml-auto bg-blue-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                          {ch.unread}
                        </span>
                      )}
                    </button>
                  ))}

                <div className="flex items-center justify-between px-2 mt-4 mb-2">
                  <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                    Direct Messages
                  </span>
                  <Plus className="w-3 h-3 text-slate-500" />
                </div>
                {channels
                  .filter((c) => c.isDM)
                  .map((ch, i) => (
                    <button
                      key={i}
                      className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm text-slate-400 hover:bg-slate-700/50 hover:text-slate-200 transition-colors"
                    >
                      <div className="relative flex-shrink-0">
                        <div className="w-4 h-4 rounded-full bg-slate-600" />
                        <div className="absolute -bottom-0.5 -right-0.5 w-2 h-2 bg-emerald-500 rounded-full border border-slate-800" />
                      </div>
                      <span className="truncate font-medium">{ch.name}</span>
                      {ch.unread > 0 && (
                        <span className="ml-auto bg-blue-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                          {ch.unread}
                        </span>
                      )}
                    </button>
                  ))}
              </div>
            </div>

            {/* Main Chat Area */}
            <div className="flex-1 flex flex-col bg-white">
              {/* Channel Header */}
              <div className="bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between flex-shrink-0 shadow-sm">
                <div className="flex items-center gap-2">
                  <Hash className="w-4 h-4 text-slate-400" />
                  <h3 className="font-bold text-base text-slate-900">
                    general
                  </h3>
                  <span className="text-xs text-slate-400 ml-1 hidden sm:inline">
                    Practice-wide updates & coordination
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button className="p-1.5 rounded-md hover:bg-slate-100 text-slate-400">
                    <Pin className="w-4 h-4" />
                  </button>
                  <button className="p-1.5 rounded-md hover:bg-slate-100 text-slate-400">
                    <Bell className="w-4 h-4" />
                  </button>
                  <button className="p-1.5 rounded-md hover:bg-slate-100 text-slate-400">
                    <Search className="w-4 h-4" />
                  </button>
                  <button className="p-1.5 rounded-md hover:bg-slate-100 text-slate-400">
                    <Users className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-5 py-4">
                <div className="space-y-5">
                  <div className="flex items-center justify-center py-2">
                    <span className="text-[11px] font-semibold text-slate-400 bg-slate-100 px-3 py-1 rounded-full uppercase tracking-wider">
                      Today — Monday, Feb 13
                    </span>
                  </div>

                  <AnimatePresence mode="popLayout" initial={false}>
                    {visibleMessages.map((msg) => (
                      <motion.div
                        key={msg.id}
                        initial={{ opacity: 0, y: 8, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        layout
                        transition={{ duration: 0.35, ease: "easeOut" }}
                        className="flex items-start gap-3 group"
                      >
                        <div
                          className={`w-9 h-9 rounded-lg ${msg.color} flex items-center justify-center text-white text-xs font-bold flex-shrink-0 shadow-sm ${
                            msg.isAI ? "ring-2 ring-violet-300" : ""
                          }`}
                        >
                          {msg.avatar}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline gap-2">
                            <span className="font-bold text-sm text-slate-900">
                              {msg.sender}
                            </span>
                            {msg.isAI && (
                              <span className="px-1.5 py-0.5 rounded bg-violet-100 text-violet-600 text-[9px] font-bold uppercase tracking-wider">
                                AI Agent
                              </span>
                            )}
                            <span className="text-[11px] text-slate-400 font-medium">
                              {msg.time}
                            </span>
                          </div>
                          <p className="text-sm text-slate-700 leading-relaxed mt-0.5">
                            {msg.content}
                          </p>
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              </div>

              {/* Input Area */}
              <div className="p-4 bg-white border-t border-slate-100">
                <div className="flex gap-2 items-end">
                  <div className="flex gap-1 pb-1">
                    <button className="p-1.5 rounded-md hover:bg-slate-100 text-slate-400">
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="flex-1 bg-slate-100 rounded-xl px-4 py-2.5 min-h-[44px] border border-transparent focus-within:border-violet-400 focus-within:ring-2 focus-within:ring-violet-100 transition-all">
                    <input
                      type="text"
                      placeholder="Message #general..."
                      className="w-full bg-transparent border-0 text-sm focus:outline-none placeholder:text-slate-400"
                    />
                  </div>
                  <div className="flex gap-1 pb-1">
                    <button className="p-1.5 rounded-md hover:bg-slate-100 text-slate-400">
                      <Smile className="w-4 h-4" />
                    </button>
                    <button className="p-1.5 rounded-md hover:bg-slate-100 text-slate-400">
                      <Paperclip className="w-4 h-4" />
                    </button>
                    <button className="p-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-white shadow-sm">
                      <Send className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default TeamChatSlide;
